import Card, { CardHeader, CardBody } from '../ui/Card';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AssignmentPicker from './AssignmentPicker';
import { assignEntity } from '../../api/aufgaben';
import type { AufgabenEntityType, AssignmentChangeBody } from '../../api/aufgaben';
import type { AssignmentInfo, CityMember } from '../../types/index';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../types/api';

interface AssignmentWidgetProps {
  entityType: AufgabenEntityType;
  entityId: number;
  /**
   * Current assignment (owner + context + when + by whom). Pass null if the
   * entity has no owner yet.
   */
  assignment: AssignmentInfo | null;
  /** Optional: query keys to invalidate after a successful change. */
  invalidateKeys?: (string | number)[][];
  /**
   * Optional override for the assignment call. Used by entities that are not
   * part of the Aufgaben list (e.g. Gruppe) but still carry a zustaendig owner.
   */
  assignFn?: (body: AssignmentChangeBody) => Promise<unknown>;
}

/**
 * Reusable "Zuständig" (Owner/Verantwortung) panel + Übergabe-Dialog.
 * Konkrete To-Dos werden separat im AufgabenBlock verwaltet.
 */
export default function AssignmentWidget({
  entityType,
  entityId,
  assignment,
  invalidateKeys = [],
  assignFn,
}: AssignmentWidgetProps) {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [pickedUserId, setPickedUserId] = useState<number | null>(assignment?.user?.id ?? null);
  const [pickedMember, setPickedMember] = useState<CityMember | null>(null);
  const [context, setContext] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const body: AssignmentChangeBody = { new_user_id: pickedUserId, context: context.trim() || null };
      return assignFn ? assignFn(body) : assignEntity(entityType, entityId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aufgaben'] });
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      reset();
    },
    onError: (err) => {
      setErrorMsg(getApiErrorMessage(err, 'Fehler beim Zuweisen'));
    },
  });

  const reset = () => {
    setEditing(false);
    setPickedUserId(assignment?.user?.id ?? null);
    setPickedMember(null);
    setContext('');
    setErrorMsg('');
  };

  const ownerChanged = pickedUserId !== (assignment?.user?.id ?? null);
  // Kontext nur Pflicht bei echtem Wechsel auf eine andere Person.
  const contextRequired =
    ownerChanged && pickedUserId !== null && currentUser !== null && pickedUserId !== currentUser.id;
  const canSubmit = ownerChanged && (!contextRequired || context.trim().length > 0);

  const current = assignment?.user;
  const assignedAt = assignment?.assigned_at
    ? new Date(assignment.assigned_at).toLocaleDateString('de-DE')
    : null;

  const targetLabel = pickedMember?.full_name ?? 'Empfänger';

  return (
    <Card>
      {/* Header */}
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-medium">Zuständig</h3>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
              current ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${current ? 'bg-blue-500' : 'bg-gray-400'}`} />
            {current ? 'Zugewiesen' : 'Niemand zuständig'}
          </span>
        </div>
        {editing ? (
          <button onClick={reset} className="btn btn-text text-neutral-600 enabled:hover:text-neutral-800 focus-visible:ring-neutral-500 shrink-0 text-sm">
            Abbrechen
          </button>
        ) : current ? (
          <button
            onClick={() => setEditing(true)}
            className="btn btn-text shrink-0 text-sm"
          >
            Übergeben
          </button>
        ) : null}
      </CardHeader>

      {/* Body */}
      <CardBody>
        {editing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Zuständig</label>
              <AssignmentPicker
                value={pickedUserId}
                onChange={(uid, m) => {
                  setPickedUserId(uid);
                  setPickedMember(m);
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {contextRequired ? `Hinweis für ${targetLabel} (Pflicht)` : 'Hinweis (optional)'}
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Kurzer Übergabe-Hinweis: Was wurde erledigt, was wird erwartet?"
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {errorMsg && <p className="text-xs text-red-600">{errorMsg}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => mutation.mutate()}
                disabled={!canSubmit || mutation.isPending}
                className="btn btn-primary"
              >
                {mutation.isPending ? 'Wird gespeichert...' : 'Übernehmen'}
              </button>
              <button
                onClick={reset}
                className="btn btn-secondary"
              >
                Zurück
              </button>
            </div>
          </div>
        ) : current ? (
          <div>
            <p className="text-sm text-gray-900">
              <span className="inline-flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center">
                  {current.full_name.charAt(0).toUpperCase()}
                </span>
                <span className="font-medium">{current.full_name}</span>
              </span>
              {assignedAt && <span className="ml-2 text-xs text-gray-500">seit {assignedAt}</span>}
            </p>
            {assignment?.context && (
              <p className="mt-2 text-sm text-gray-600 italic border-l-2 border-blue-200 pl-2">
                „{assignment.context}"
              </p>
            )}
            {assignment?.assigned_by && assignment.assigned_by.id !== current.id && (
              <p className="mt-1 text-xs text-gray-500">
                übergeben von {assignment.assigned_by.full_name}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">Für diesen Eintrag ist noch niemand zuständig.</p>
            <button
              onClick={() => setEditing(true)}
              className="btn btn-primary shrink-0"
            >
              Zuweisen
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
